import createAuth0Client from '@auth0/auth0-spa-js';
import * as Sentry from '@sentry/vue';
import jwtDecode from 'jwt-decode';
import Vue from 'vue';

import { queryStringToObject } from '~/components/tool/Query';
import { UserRole } from '../components/settings/User';
import { clearTimelineToStorage } from '~/components/tool/Timeline';
import {
  isDelegatedSurveyPath,
  isWellnessCloudEnabled,
  toWellnessCloudLoginUrl,
  toWellnessCloudLogoutUrl,
} from '~/utils/wellnesscloud';
import {
  isWellnessCloudRefreshDue,
  markWellnessCloudRefreshed,
  refreshWellnessCloudSession,
  resetWellnessCloudUserMismatch,
  resumeWellnessCloudRefresh,
} from '~/utils/wellnesscloudSession';

/** Define a default action to perform after authentication */
// const DEFAULT_REDIRECT_CALLBACK = () => window.history.replaceState({}, document.title, window.location.pathname);

let instance;

const TOKEN_USER_STORAGE_KEY = 'auth_token_sub';

const toTokenSub = (token: string | null): string | null => {
  if (!token) {
    return null;
  }
  try {
    return jwtDecode<{ sub?: string }>(token).sub ?? null;
  } catch {
    return null;
  }
};

/** Returns the current instance of the SDK */
export const getInstance = () => instance;

/** Creates an instance of the Auth0 SDK. If one has already been created, it returns that instance */
/* eslint-disable prefer-const, @typescript-eslint/naming-convention */
export const useAuth0 = ({ onRedirectCallback, ...options }, context) => {
  if (instance) return instance;

  // The 'instance' is simply a Vue object
  instance = new Vue({
    data() {
      return {
        loading: true,
        isAuthenticated: null,
        user: {},
        auth0Client: null,
        popupOpen: false,
        error: null,
        token: null,
        companyUser: null,
      };
    },
    /** Use this lifecycle method to instantiate the SDK client */
    async created(): Promise<void> {
      if (process.env.DEVELOPMENT_TYPE === 'static') {
        setTimeout(() => {
          this.isAuthenticated = true;
          this.token = 'test';
          this.getMe();
        }, 1000);
        setTimeout(() => {
          this.loading = false;
        }, 2000);
        return;
      }
      // Create a new instance of the SDK client using members of the given options object
      this.auth0Client = await createAuth0Client({
        domain: options.domain,
        client_id: options.clientId,
        audience: options.audience,
        // scope: options.scope,
        redirect_uri: options.redirectUri,
        useRefreshTokens: true, // トークン更新をリフレッシュトークンで行う
        cacheLocation: 'localstorage', // 格納先はlocalstorage（ページリロード時も保持されるため高速）
      });

      let isLoginCallback = false;
      try {
        // If the user is returning to the app after authentication..
        if (
          window.location.search.includes('code=')
          && window.location.search.includes('state=')
        ) {
          // handle the redirect and retrieve tokens
          const { appState } = await this.auth0Client.handleRedirectCallback();
          isLoginCallback = true;
          localStorage.removeItem(TOKEN_USER_STORAGE_KEY);
          // getMe()より前に書く。委譲先の更新はgetMe()で従業員と判定できた後にしか走らないため、
          // ログイン直後の往復の最中に委譲先を更新することがなくなる
          if (isWellnessCloudEnabled()) {
            markWellnessCloudRefreshed();
            resumeWellnessCloudRefresh();
            resetWellnessCloudUserMismatch();
          }

          this.error = null;

          // Notify subscribers that the redirect callback has happened, passing the appState
          // (useful for retrieving any pre-authentication state)
          onRedirectCallback(appState);
        }
      } catch (e) {
        this.error = e;
      } finally {
        // Initialize our internal authentication state
        this.isAuthenticated = await this.auth0Client.isAuthenticated();
        if (this.isAuthenticated) {
          this.token = await this.auth0Client.getTokenSilently();
          if (this.isTokenUserChanged()) {
            this.logoutForUserChange();
            await new Promise<never>(() => undefined);
          }

          await this.getMe();

          if (isLoginCallback && this.isWellnessCloudTarget()) {
            // code/stateを残すと、戻ってきた読み込みでhandleRedirectCallbackがもう一度走って失敗する
            window.location.replace(toWellnessCloudLoginUrl(window.location.pathname));
            // ウェルカム転送とauth_redirectの消費は、委譲先から戻ってきた読み込みで行わせる。
            // finally内のreturnはno-unsafe-finallyに当たるため、解決しないPromiseで後続を止める
            await new Promise<never>(() => undefined);
          }
          this.requestWellnessCloudRefresh();

          Sentry.setTag('userid', this.user.id);
          Sentry.setTag('email', this.user.mailaddress);

          await this.getCompanyUserMe();

          this.loading = false;

          localStorage.setItem('role', this.user.role);

          // 初回アンケート未回答チェックをAuth0側で実施し、必要ならウェルカム面談に転送
          let didWelcomeRedirect = false;
          try {
            if (context?.$s && context?.$api) {
              await context.$s.initProject();
              const firstProjectId = await context.$s.getProjectIdByProjectName(
                'デフォルトプロジェクト',
              );
              if (firstProjectId) {
                const res = await context.$api.getEnquetesByProjectId(
                  firstProjectId,
                  { limit: 1, page: 1 },
                );
                const hasAnswered = (Array.isArray(res) && res.length > 0)
                  || (Array.isArray(res?.data) && res.data.length > 0)
                  || (typeof res?.total === 'number' && res.total > 0);
                const isWelcomeRoute = [
                  '/welcome',
                  '/welcome-interview',
                ].includes(context.route.path);
                if (!hasAnswered && !isWelcomeRoute) {
                  const visited = localStorage.getItem('welcome_interview_visited')
                    === 'true';
                  if (visited) {
                    await context.app.router.push('/welcome-interview');
                  } else {
                    await context.app.router.push('/welcome');
                  }
                  didWelcomeRedirect = true;
                }
              }
            }
          } catch (e) {
            // 失敗しても通常のフローで継続
          }

          // ウェルカム面談に転送した場合は、redirectPathでの転送は回避するためクリア
          if (didWelcomeRedirect) {
            localStorage.removeItem('auth_redirect');
          }

          const redirectPath = localStorage.getItem('auth_redirect');
          if (redirectPath) {
            // DxAuthからのリダイレクトをした==ログインに成功した として扱いログイン通知メールを送る
            // セッション更新のタイミングでリダイレクトが発生し得ることに注意
            await context.$api.sendLoginNotificationMail();

            localStorage.removeItem('auth_redirect');
            // 委譲対象へ戻すと別オリジンへ遷移し、委譲先が未起動だと本アプリへ戻れなくなる
            const isDelegated = this.user.role === UserRole.Patient
              && isDelegatedSurveyPath(context.app.router, redirectPath);
            if (!isDelegated) {
              context.redirect(redirectPath);
            }
          }
        } else if (
          // local 開発時はログインできるので loginWithRedirect 可
          process.env.NODE_ENV === 'development'
          // dstation.thesss.net へのアップ時以外は loginWithRedirect 可
          || process.env.DEVELOPMENT_TYPE !== 'static'
        ) {
          // ログイン不必要ページは loginWithRedirect しない
          if (!context.route.meta[0]?.permissions?.includes(UserRole.Guest)) {
            let role = options.redirectUri.split('role=')[1];
            role = role ? role.split('&')[0] : '';

            const redirectPath = context.route.fullPath;
            // 新規登録時保存されていたpathを上書きしないように設定
            if (!['/welcome-interview', '/welcome'].includes(redirectPath)) {
              // 前回ログインした際のロールが残ってしまう事象が発生するため、role指定付きのurlは保存しない。
              if (!redirectPath.includes('role=')) {
                localStorage.setItem('auth_redirect', redirectPath);
              }
            }

            // registration_info がある場合はそちらを優先
            if (options.redirectUri.includes('registration_info')) {
              const registrationInfo = options.redirectUri.split(
                'registration_info=',
              )[1];
              context.redirect(`/login?registration_info=${registrationInfo}`);
            } else {
              // getLoginRedirectUrl()を使用してリダイレクト先を決定
              // role パラメータがURLにある場合はそれを考慮
              let loginUrl = '/login';
              if (role === UserRole.Doctor) {
                loginUrl = '/login?role=doctor';
              } else {
                // role パラメータがない場合は、現在のパスに基づいて判定
                loginUrl = this.getLoginRedirectUrl(redirectPath);
              }
              context.redirect(loginUrl);
            }
          }
        }
      }
    },
    methods: {
      /** Handles the callback when logging in using a redirect */
      async handleRedirectCallback(): Promise<void> {
        this.loading = true;
        try {
          await this.auth0Client.handleRedirectCallback();
          this.user = await this.auth0Client.getUser();
          this.isAuthenticated = true;
          this.error = null;
        } catch (e) {
          this.error = e;
        } finally {
          this.loading = false;
        }
      },

      /**
       * 特定のパスでlocalStorage.roleを優先するかどうかを判定
       *
       * @param path - 現在のパス
       * @returns localStorage.roleを優先する場合はtrue
       */
      shouldUseLocalStorageRole(path: string): boolean {
        // リロード or 進む・戻るの場合は同一セッションの可能性が高いため、localStorageのroleを利用する
        const isReloadOrHistory = isReloadOrBackForward();
        if (isReloadOrHistory) return true;
        // 将来的に特定のパスでlocalStorage.roleを使用する場合はここに追加
        // 例: チャット機能など
        const roleEnabledPaths = [
          // '/chat',  // チャット機能で医師/患者を区別する場合
          // '/consultation',  // オンライン診療など
        ];

        return roleEnabledPaths.some(enabledPath => path.startsWith(enabledPath));
      },

      /**
       * リダイレクト先URLを決定
       *
       * @param currentPath - 現在アクセスしようとしているパス
       * @returns リダイレクト先のログインURL
       */
      getLoginRedirectUrl(currentPath: string): string {
        // 特定のパスの場合のみlocalStorage.roleを参照
        if (this.shouldUseLocalStorageRole(currentPath)) {
          const role = localStorage.getItem('role');
          if (role && role !== UserRole.Patient) {
            return '/login?role=doctor';
          }
        }

        // デフォルトは患者ログイン画面
        return '/login';
      },

      /** Authenticates the user using the redirect method */
      loginWithRedirect(o) {
        return this.auth0Client.loginWithRedirect(o);
      },

      /** Returns the access token. If the token is invalid or missing, a new one is retrieved */
      getTokenSilently(o) {
        return this.auth0Client.getTokenSilently(o);
      },

      /** Logs the user out and removes their session on the authorization server */
      logout(o) {
        clearTimelineToStorage();

        localStorage.removeItem('auth_redirect');
        localStorage.removeItem(TOKEN_USER_STORAGE_KEY);

        // WellnessCloud側にもトークンキャッシュが残るため、Auth0のログアウト後に経由して破棄する。
        const returnTo = o?.returnTo || window.location.origin;
        const viaWellnessCloud = toWellnessCloudLogoutUrl(returnTo);
        const options = viaWellnessCloud ? { ...(o || {}), returnTo: viaWellnessCloud } : o;

        return this.auth0Client.logout(options);
      },

      async getMe(): Promise<void> {
        // eslint-disable-next-line no-async-promise-executor
        const user = await context.$api.getProfile();
        this.user = user;
      },

      isWellnessCloudTarget(): boolean {
        return this.user?.role === UserRole.Patient && isWellnessCloudEnabled();
      },

      // 本アプリがリフレッシュトークンを使ったかはauth0-spa-jsから取れず、読み込み直後の更新を見逃すため、
      // トークン取得のたびに前回の更新からの経過時間で判定する。更新の完了はAPIリクエストを遅らせないため待たない
      requestWellnessCloudRefresh(): void {
        if (!this.isWellnessCloudTarget() || !isWellnessCloudRefreshDue()) {
          return;
        }
        refreshWellnessCloudSession(toTokenSub(this.token));
      },

      // リフレッシュトークンが失効すると、auth0-spa-jsはその時点のSSOセッションのユーザーでトークンを取り直すため、
      // 別ユーザーのトークンに入れ替わりうる。画面のユーザー情報とAPIに送るトークンの持ち主が食い違うので、ログアウトさせる
      isTokenUserChanged(): boolean {
        const sub = toTokenSub(this.token);
        if (!sub) {
          return false;
        }
        const loggedInSub = localStorage.getItem(TOKEN_USER_STORAGE_KEY);
        if (!loggedInSub) {
          localStorage.setItem(TOKEN_USER_STORAGE_KEY, sub);
          return false;
        }
        return loggedInSub !== sub;
      },

      logoutForUserChange(): void {
        let returnTo = window.location.origin;
        const role = localStorage.getItem('role');
        if (role && role !== UserRole.Patient) {
          returnTo += `?role=${UserRole.Doctor}`;
        }
        this.logout({ returnTo });
      },

      async getCompanyUserMe(): Promise<void> {
        const companyUser = await context.$api.getCompanyUserMe();
        this.companyUser = companyUser;
      },

      /** アクセストークンを更新する。キャッシュが残っている場合はキャッシュを利用する */
      async updateTokenWithCache(): Promise<string> {
        this.token = await this.auth0Client.getTokenSilently({
          ignoreCache: false,
        });
        if (this.isTokenUserChanged()) {
          this.logoutForUserChange();
          return this.token;
        }
        this.requestWellnessCloudRefresh();
        return this.token;
      },
      /** アクセストークンを更新する。キャッシュが残っている場合も強制的に更新する */
      async updateToken(): Promise<void> {
        this.token = await this.auth0Client.getTokenSilently({
          ignoreCache: true,
        });
        if (this.isTokenUserChanged()) {
          this.logoutForUserChange();
          return;
        }
        this.requestWellnessCloudRefresh();
      },
    },
  });

  return instance;
};

/**
 * ページアクセスがリロードまたは履歴ナビゲーション（戻る/進む）によるものかを判定
 *
 * @returns リロードまたは戻る/進むボタンによるアクセスの場合は true
 */
function isReloadOrBackForward(): boolean {
  const perfEntries = performance.getEntriesByType("navigation") as PerformanceNavigationTiming[];

  if (perfEntries.length === 0) return false;

  const navType = perfEntries[0].type;

  if (navType === 'reload') return true;
  if (navType === 'back_forward') return true;

  return false;
}
export default async function (context, inject) {
  let redirectUri = window.location.origin;

  if (window && window.location.search) {
    const prams = queryStringToObject(window.location.search);

    // DXAuthからリダイレクトされる際に patient%3Fopt%3Dredirect のように?がエンコードされている場合があるためdecodeURIComponentでデコードする
    const role = decodeURIComponent(prams.role || '').split('?')[0];
    if (role && role !== UserRole.Patient) {
      redirectUri += `?role=doctor`;
    }

    if (prams.registration_info) {
      redirectUri += `registration_info=${prams.registration_info}`;
    }
  }
  const options = {
    domain: context.env.AUTH0_DOMAIN,
    clientId: context.env.AUTH0_CLIENT_ID,
    audience: context.env.AUTH0_AUDIENCE,
    redirectUri,
    onRedirectCallback: (appState) => {
      context.app.router.push(
        appState && appState.targetUrl
          ? appState.targetUrl
          : window.location.pathname,
      );
    },
  };

  const $auth0 = await useAuth0({ ...options }, context);
  inject('auth0', $auth0);
}
